import { FavoriteFontModel } from "../types/customFontTypes";
import { auth } from "../Config/firebase-config";
import {
  loadFavoritesFromCache,
  saveFavoritesToCache,
  flushPendingWrites,
} from "./favoriteFontCache";

export class FavoriteFontManager {
  private static instance: FavoriteFontManager;
  private favorites: FavoriteFontModel[] = [];
  private listeners: Set<(favs: FavoriteFontModel[]) => void> = new Set();

  private constructor() {
    if (typeof window !== "undefined") {
      auth.onAuthStateChanged(async (user) => {
        if (user) {
          await this.loadFavoritesForUser(user.uid);
        } else {
          this.clearOnLogout();
        }
      });
    }
  }

  public static getInstance(): FavoriteFontManager {
    if (!FavoriteFontManager.instance) {
      FavoriteFontManager.instance = new FavoriteFontManager();
    }
    return FavoriteFontManager.instance;
  }

  public subscribe(listener: (favs: FavoriteFontModel[]) => void): () => void {
    this.listeners.add(listener);
    listener(this.favorites);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((l) => l([...this.favorites]));
  }

  public async loadFavoritesForUser(uid: string): Promise<void> {
    this.favorites = await loadFavoritesFromCache(uid);
    this.notify();
  }

  public getFavorites(): FavoriteFontModel[] {
    return [...this.favorites];
  }

  public isFavorite(fontName: string): boolean {
    const target = fontName.trim().toLowerCase();
    return this.favorites.some(
      (f) => f.fontName.toLowerCase() === target || f.displayName?.toLowerCase() === target
    );
  }

  public toggleFavorite(
    fontName: string,
    displayName: string,
    isCustom = false,
    fileName?: string
  ): boolean {
    const uid = auth.currentUser?.uid;
    const target = fontName.trim().toLowerCase();
    const idx = this.favorites.findIndex(
      (f) => f.fontName.toLowerCase() === target || f.displayName?.toLowerCase() === target
    );

    if (idx !== -1) {
      this.favorites.splice(idx, 1);
    } else {
      this.favorites.push({
        id: fontName,
        fontName,
        displayName: displayName || fontName,
        isCustom,
        fileName,
        dateFavorited: Math.floor(Date.now() / 1000),
      });
    }

    this.notify();
    if (uid) {
      void saveFavoritesToCache(uid, this.favorites);
    }
    return idx === -1;
  }

  public removeFavorite(fontName: string): void {
    const uid = auth.currentUser?.uid;
    const target = fontName.trim().toLowerCase();
    this.favorites = this.favorites.filter(
      (f) => f.fontName.toLowerCase() !== target && f.displayName?.toLowerCase() !== target
    );
    this.notify();
    if (uid) {
      void saveFavoritesToCache(uid, this.favorites);
    }
  }

  public async flush(): Promise<void> {
    await flushPendingWrites();
  }

  public async clearOnLogout(): Promise<void> {
    await flushPendingWrites();
    this.favorites = [];
    this.notify();
  }

  public clearCustomFontsOnLogout(): void {
    void this.clearOnLogout();
  }
}

export default FavoriteFontManager.getInstance();
